import React from 'react'
import { FeeAmount } from '@uniswap/v3-sdk'
import { useTranslation } from 'react-i18next'
import { AutoColumn } from 'components/Column'
import { DynamicSection } from 'pages/AddLiquidity/styled'
import { TYPE } from 'theme'
import { AutoRow } from 'components/Row'
import { ButtonOutlined, ButtonRadioChecked } from 'components/Button'
import styled from 'styled-components'
import TagButton from 'components/TagButton'

const ResponsiveText = styled(TYPE.label)`
  ${({ theme }) => theme.mediaWidth.upToSmall`
    font-size: 12px;
  `};
`
const GridContainer = styled.div`
  margin: auto;
  display: grid;
  grid-template-columns: repeat(2, 1fr); /* 2 items per row */
  gap: 12px;
  width: 100%;
`
const FadeContainer = styled.div`
  opacity: 0;
  max-height: 0;
  overflow: hidden;
  transition: opacity 0.3s ease-in-out, max-height 0.3s ease-in-out;

  &.open {
    opacity: 1;
    max-height: 500px;
  }
`

export default function FeeSelector({
  disabled = false,
  feeAmount,
  handleFeePoolSelect,
}: {
  disabled?: boolean
  feeAmount?: FeeAmount
  handleFeePoolSelect: (feeAmount: FeeAmount) => void
}) {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = React.useState(false)

  const DESCRIPTIONS = {
    [FeeAmount.LOWER]: 'Best for very stable pairs.',
    [FeeAmount.LOW]: 'Best for stable pairs.',
    [FeeAmount.MEDIUM]: 'Best for most pairs.',
    [FeeAmount.HIGH]: 'Best for exotic pairs.',
  }

  return (
    <AutoColumn gap="16px">
      <DynamicSection gap="md" disabled={disabled}>
        <TYPE.label>{t('selectPool')}</TYPE.label>
        <TYPE.main fontSize={14} fontWeight={400} style={{ marginBottom: '.5rem', lineHeight: '125%' }}>
          Select a pool type based on your preferred liquidity provider fee.
        </TYPE.main>

        <AutoRow>
          <ButtonOutlined
            onClick={() => setIsOpen(!isOpen)}
            style={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <ResponsiveText>
                {feeAmount && feeAmount / 10000}% {t('fee')}
              </ResponsiveText>
              <TYPE.main fontWeight={400} fontSize="12px" textAlign="left">
                {feeAmount ? DESCRIPTIONS[feeAmount] : ''}
              </TYPE.main>
            </div>
            <TagButton onClick={() => setIsOpen(!isOpen)}>
              {isOpen ? (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="icon icon-tabler icons-tabler-outline icon-tabler-chevron-up"
                  >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M6 15l6 -6l6 6" />
                  </svg>
                  Hide
                </>
              ) : (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="icon icon-tabler icons-tabler-outline icon-tabler-chevron-down"
                  >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M6 9l6 6l6 -6" />
                  </svg>
                  More
                </>
              )}
            </TagButton>
          </ButtonOutlined>
        </AutoRow>
        {isOpen && (
          <FadeContainer className={isOpen ? 'open' : ''}>
            <GridContainer>
              <ButtonRadioChecked
                width="100%"
                active={feeAmount === FeeAmount.LOWER}
                onClick={() => handleFeePoolSelect(FeeAmount.LOWER)}
              >
                <AutoColumn gap="sm" justify="flex-start">
                  <ResponsiveText>0.01% {t('fee')}</ResponsiveText>
                  <TYPE.main fontWeight={400} fontSize="12px" textAlign="left">
                    Best for very stable pairs.
                  </TYPE.main>
                </AutoColumn>
              </ButtonRadioChecked>
              <ButtonRadioChecked
                width="100%"
                active={feeAmount === FeeAmount.LOW}
                onClick={() => handleFeePoolSelect(FeeAmount.LOW)}
              >
                <AutoColumn gap="sm" justify="flex-start">
                  <ResponsiveText>0.05% {t('fee')}</ResponsiveText>
                  <TYPE.main fontWeight={400} fontSize="12px" textAlign="left">
                    Best for stable pairs.
                  </TYPE.main>
                </AutoColumn>
              </ButtonRadioChecked>
              <ButtonRadioChecked
                width="100%"
                active={feeAmount === FeeAmount.MEDIUM}
                onClick={() => handleFeePoolSelect(FeeAmount.MEDIUM)}
              >
                <AutoColumn gap="sm" justify="flex-start">
                  <ResponsiveText>0.3% {t('fee')}</ResponsiveText>
                  <TYPE.main fontWeight={400} fontSize="12px" textAlign="left">
                    Best for most pairs.
                  </TYPE.main>
                </AutoColumn>
              </ButtonRadioChecked>
              <ButtonRadioChecked
                width="100%"
                active={feeAmount === FeeAmount.HIGH}
                onClick={() => handleFeePoolSelect(FeeAmount.HIGH)}
              >
                <AutoColumn gap="sm" justify="flex-start">
                  <ResponsiveText>1% {t('fee')}</ResponsiveText>
                  <TYPE.main fontWeight={400} fontSize="12px" textAlign="left">
                    Best for exotic pairs.
                  </TYPE.main>
                </AutoColumn>
              </ButtonRadioChecked>
            </GridContainer>
          </FadeContainer>
        )}
      </DynamicSection>
    </AutoColumn>
  )
}
